import { SENTINEL_LOCATION_IDS } from '../constants/search';

/**
 * Transform legacy search data to the RecentSearch format expected by @homeexchange/dashboard
 * @param {Object} legacySearch - The legacy search data from API
 * @returns {Object} RecentSearch in the expected format
 */
export const transformSearchToRecentSearch = (legacySearch) => {
    if (!legacySearch) {
        return null;
    }

    const query = legacySearch.query || {};
    const location = query.location || {};
    const calendar = query.calendar || {};
    const home = query.home || {};
    const guestpoints = query.guestpoints || {};
    const host = query.host || {};

    // Build the RecentSearch object
    const recentSearch = {
        id: String(legacySearch.id || ''),
        label: legacySearch.place || ''
    };

    // Location
    if (Object.keys(location).length > 0) {
        recentSearch.location = {};

        if (location.bounds) {
            recentSearch.location.bounds = {
                ne: {
                    lat: parseFloat(location.bounds.north) || parseFloat(location.bounds.ne?.lat) || 0,
                    lon: parseFloat(location.bounds.east) || parseFloat(location.bounds.ne?.lon) || 0
                },
                sw: {
                    lat: parseFloat(location.bounds.south) || parseFloat(location.bounds.sw?.lat) || 0,
                    lon: parseFloat(location.bounds.west) || parseFloat(location.bounds.sw?.lon) || 0
                }
            };
        }

        const polygonLocationId = location.polygon?.location_id;
        if (polygonLocationId && !SENTINEL_LOCATION_IDS.includes(String(polygonLocationId))) {
            recentSearch.location.polygon = {
                location_id: polygonLocationId,
                provider: location.polygon.provider
            };
        }

        if (location.country) {
            recentSearch.location.country = location.country;
        }

        if (location.zoom) {
            recentSearch.location.zoom = location.zoom;
        }

        if (location.label || legacySearch.place) {
            recentSearch.location.label = location.label || legacySearch.place;
        }
    }

    // Calendar
    if (Object.keys(calendar).length > 0) {
        recentSearch.calendar = {};

        if (calendar.date_ranges && calendar.date_ranges.length > 0) {
            recentSearch.calendar.date_ranges = calendar.date_ranges.map((range) => ({
                from: range.from || range.start || '',
                to: range.to || range.end || ''
            }));
        }

        if (calendar.flexibility !== undefined && calendar.flexibility !== null) {
            recentSearch.calendar.flexibility = calendar.flexibility;
        }

        if (calendar.exchange_types && calendar.exchange_types.length > 0) {
            recentSearch.calendar.exchange_types = calendar.exchange_types;
        }

        if (calendar.duration && Object.keys(calendar.duration).length > 0) {
            recentSearch.calendar.duration = {
                kind: calendar.duration.kind,
                number_of_nights: calendar.duration.number_of_nights
            };
        }

        if (calendar.last_minute !== undefined) {
            recentSearch.calendar.last_minute = calendar.last_minute;
        }
    }

    // Home
    if (Object.keys(home).length > 0) {
        recentSearch.home = {};

        if (home.size && Object.keys(home.size).length > 0) {
            recentSearch.home.size = {};

            if (home.size.beds && Object.keys(home.size.beds).length > 0) {
                recentSearch.home.size.beds = {
                    adults: home.size.beds.adults,
                    children: home.size.beds.children,
                    babies: home.size.beds.babies
                };
            }

            if (home.size.bedrooms !== undefined) {
                recentSearch.home.size.bedrooms = home.size.bedrooms;
            }

            if (home.size.bathrooms !== undefined) {
                recentSearch.home.size.bathrooms = home.size.bathrooms;
            }
        }

        if (home.amenities && home.amenities.length > 0) {
            recentSearch.home.amenities = home.amenities;
        }

        if (home.surrounding && home.surrounding.length > 0) {
            recentSearch.home.surrounding = home.surrounding;
        }

        if (home.type) {
            recentSearch.home.type = home.type;
        }

        if (home.is_primary !== undefined) {
            recentSearch.home.is_primary = home.is_primary;
        }

        if (home.is_private_room !== undefined) {
            recentSearch.home.is_private_room = home.is_private_room;
        }

        if (home.exclude_animals && home.exclude_animals.length > 0) {
            recentSearch.home.exclude_animals = home.exclude_animals;
        }

        if (home.eco_level !== undefined) {
            recentSearch.home.eco_level = home.eco_level;
        }
    }

    // Guestpoints
    if (Object.keys(guestpoints).length > 0) {
        recentSearch.guestpoints = {
            from: guestpoints.from,
            to: guestpoints.to
        };
    }

    // Host
    if (host.groups && host.groups.length > 0) {
        recentSearch.host = {
            groups: host.groups
        };
    }

    // Additional fields
    if (query.placeId || legacySearch.place_id) {
        recentSearch.placeId = query.placeId || legacySearch.place_id;
    }

    if (query.luxury !== undefined) {
        recentSearch.luxury = query.luxury;
    }

    if (query.favorite !== undefined) {
        recentSearch.favorite = query.favorite;
    }

    if (query.reverse && query.reverse.length > 0) {
        recentSearch.reverse = query.reverse;
    }

    return recentSearch;
};

export const deduplicateSearches = (searches) => {
    const seen = new Set();
    return searches.filter((search) => {
        if (seen.has(search.place)) return false;
        seen.add(search.place);
        return true;
    });
};

export const transformSearchesToRecentSearches = (legacySearches) => {
    if (!legacySearches || !Array.isArray(legacySearches)) {
        return [];
    }
    return deduplicateSearches(legacySearches).slice(0, 3).map(transformSearchToRecentSearch).filter(Boolean);
};
